import { Button, TextField } from "@dash/form";
import { Page, PageHeader } from "@dash/page";
import { DEV_POINTS_URL, LOCAL_POINTS_URL, useConfig } from "@megarax/dev-config";
import { format } from "date-fns";
import { useState } from "react";
import QRCode from "react-qr-code";

import { useClientManager } from "@megaron/react-clients";

export const TestCodesPage = () => {
  const { config } = useConfig();

  return (
    <Page>
      <PageHeader>Testowe kody QR</PageHeader>
      {config.pointsUrl === DEV_POINTS_URL || config.pointsUrl === LOCAL_POINTS_URL ? (
        <TestCodesGenerator key={config.pointsUrl} />
      ) : (
        <p role="alert">
          Generator jest dostępny tylko dla środowiska testowego lub lokalnego. Zmień środowisko usługi punktów w
          ustawieniach deweloperskich.
        </p>
      )}
    </Page>
  );
};

const TestCodesGenerator = () => {
  const [quantity, setQuantity] = useState("10");
  const [groupName, setGroupName] = useState(() => `test_${format(new Date(), "yyyy-MM-dd")}_1`);
  const [validationError, setValidationError] = useState<string>();
  const mutation = useClientManager("loyaltyPoints").generateProductCodes().useMutation();

  const generate = () => {
    if (mutation.isLoading) return;
    if (!groupName.trim()) {
      setValidationError("Podaj nazwę grupy.");
      return;
    }
    const count = Number(quantity);
    if (!/^\d+$/.test(quantity) || !Number.isSafeInteger(count) || count < 1 || count > 100) {
      setValidationError("Podaj całkowitą liczbę kodów od 1 do 100.");
      return;
    }
    setValidationError(undefined);
    mutation.mutate({ quantity: count, groupNames: [groupName.trim()] });
  };

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <p>
        Wygeneruj od 1 do 100 kodów do testowania skanowania. Kody są zapisywane w wybranym środowisku i mają status
        pustych kodów — przed wymianą na punkty wymagają aktywacji.
      </p>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          generate();
        }}
        css={{ display: "flex", alignItems: "end", flexWrap: "wrap", gap: "1rem" }}
      >
        <TextField label="Nazwa grupy" value={groupName} onChange={setGroupName} isDisabled={mutation.isLoading} />
        <TextField
          label="Liczba kodów"
          value={quantity}
          onChange={setQuantity}
          inputElementProps={{ inputMode: "numeric" }}
          isDisabled={mutation.isLoading}
        />
        <Button isLoading={mutation.isLoading} isDisabled={mutation.isLoading}>
          Generuj kody
        </Button>
      </form>
      {validationError && <p role="alert">{validationError}</p>}
      {mutation.isError && <p role="alert">Nie udało się wygenerować kodów. {String(mutation.error)}</p>}
      {mutation.data && (
        <>
          <p role="status">Wygenerowano kodów: {mutation.data.length}. Podgląd jest dostępny do opuszczenia strony.</p>
          <div css={{ display: "flex", flexWrap: "wrap", gap: "2rem" }}>
            {mutation.data.map(({ id, key }, index) => (
              <section
                key={id}
                aria-label={`Kod ${index + 1}: ${id}`}
                css={{ padding: "1rem", border: "1px solid #ccc", borderRadius: "0.5rem" }}
              >
                <h2>
                  {index + 1}. {id}
                </h2>
                <div css={{ display: "flex", flexWrap: "wrap", gap: "1.5rem" }}>
                  <CodePreview label="Kod zewnętrzny" code={id} />
                  <CodePreview label="Kod wewnętrzny" code={id + key} />
                </div>
              </section>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const CodePreview = ({ label, code }: { label: string; code: string }) => (
  <div css={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.5rem" }}>
    <h3>{label}</h3>
    <div css={{ padding: "16px", background: "#fff" }}>
      <QRCode value={`https://kod.smig.pl/${code}`} size={192} title={`${label}: ${code}`} />
    </div>
    <code>{code}</code>
  </div>
);
